import React, { useEffect, useRef, useState, useCallback } from "react";
import { ChevronLeft, ChevronRight, Circle, Eraser, Lock, Guitar } from "lucide-react";
import { useStudio } from "@/studio/StudioContext";
import { INSTRUMENT_LIST, INSTRUMENTS } from "@/audio/engine";
import InfoDot from "@/studio/InfoDot";

const WHITE_PC = [0, 2, 4, 5, 7, 9, 11];
const BLACK_PC = [1, 3, 6, 8, 10];
const NOTE_NAMES = ["C", "C#", "D", "D#", "E", "F", "F#", "G", "G#", "A", "A#", "B"];
const VISIBLE_OCTAVES = 3;

// QWERTY offsets from the current base octave's C
const KEY_MAP = {
  z: 0, s: 1, x: 2, d: 3, c: 4, v: 5, g: 6, b: 7, h: 8, n: 9, j: 10, m: 11,
  q: 12, "2": 13, w: 14, "3": 15, e: 16, r: 17, "5": 18, t: 19, "6": 20, y: 21, "7": 22, u: 23, i: 24,
};
const OFFSET_LABEL = {
  0: "Z", 1: "S", 2: "X", 3: "D", 4: "C", 5: "V", 6: "G", 7: "B", 8: "H", 9: "N", 10: "J", 11: "M",
  12: "Q", 13: "2", 14: "W", 15: "3", 16: "E", 17: "R", 18: "5", 19: "T", 20: "6", 21: "Y", 22: "7", 23: "U", 24: "I",
};

const midiToFreq = (m) => 440 * Math.pow(2, (m - 69) / 12);

const SCALES = {
  major: { label: "Major", intervals: [0, 2, 4, 5, 7, 9, 11] },
  minor: { label: "Minor", intervals: [0, 2, 3, 5, 7, 8, 10] },
  dorian: { label: "Dorian", intervals: [0, 2, 3, 5, 7, 9, 10] },
  majPent: { label: "Major Pentatonic", intervals: [0, 2, 4, 7, 9] },
  minPent: { label: "Minor Pentatonic", intervals: [0, 3, 5, 7, 10] },
  blues: { label: "Blues", intervals: [0, 3, 5, 6, 7, 10] },
};

function chordQuality(thirdInt, fifthInt) {
  if (thirdInt === 4 && fifthInt === 7) return "";
  if (thirdInt === 3 && fifthInt === 7) return "m";
  if (thirdInt === 3 && fifthInt === 6) return "dim";
  if (thirdInt === 4 && fifthInt === 8) return "aug";
  return "";
}

// Build diatonic triads by stacking scale tones (works for any scale length).
function buildChords(scaleRootMidi, intervals) {
  const n = intervals.length;
  const tone = (j) => scaleRootMidi + (j < n ? intervals[j] : intervals[j - n] + 12);
  const chords = [];
  for (let i = 0; i < n; i++) {
    const notes = [tone(i), tone(i + 2), tone(i + 4)];
    const suffix = chordQuality(notes[1] - notes[0], notes[2] - notes[0]);
    const rootName = NOTE_NAMES[notes[0] % 12];
    chords.push({ notes, label: rootName + suffix });
  }
  return chords;
}

export default function PianoSynth() {
  const { engine } = useStudio();
  const voices = useRef({});
  const [active, setActive] = useState({});
  const [instrument, setInstrument] = useState("grandreal");
  const [pianoLoading, setPianoLoading] = useState(false);
  const [view, setView] = useState("piano");
  const [baseOctave, setBaseOctave] = useState(4);
  const [recNotes, setRecNotes] = useState(false);
  const [scaleLock, setScaleLock] = useState(false);
  const [rootPc, setRootPc] = useState(0);
  const [scaleId, setScaleId] = useState("minPent");
  const heldKeys = useRef({});

  const baseMidi = (baseOctave + 1) * 12; // C of base octave (C4 = 60)

  const intervals = SCALES[scaleId].intervals;
  const allowedPcs = new Set(intervals.map((i) => (rootPc + i) % 12));
  const isAllowed = (midi) => allowedPcs.has(((midi % 12) + 12) % 12);
  const nearestAllowed = (midi) => {
    if (isAllowed(midi)) return midi;
    for (let d = 1; d <= 6; d++) {
      if (isAllowed(midi - d)) return midi - d;
      if (isAllowed(midi + d)) return midi + d;
    }
    return midi;
  };
  const scaleRootMidi = baseMidi + rootPc;
  const chords = buildChords(scaleRootMidi, intervals);

  const noteOn = useCallback(
    (midi) => {
      if (voices.current[midi]) return;
      engine.ensureStarted();
      if (engine.ctx.state === "suspended") engine.ctx.resume();
      voices.current[midi] = engine.playSynthNote(midiToFreq(midi));
      engine.captureNoteOn(midi);
      setActive((a) => ({ ...a, [midi]: true }));
    },
    [engine]
  );

  const noteOff = useCallback(
    (midi) => {
      if (!voices.current[midi]) return;
      engine.releaseSynthNote(voices.current[midi]);
      engine.captureNoteOff(midi);
      delete voices.current[midi];
      setActive((a) => {
        const n = { ...a };
        delete n[midi];
        return n;
      });
    },
    [engine]
  );

  const pressKey = (midi) => {
    if (scaleLock && !isAllowed(midi)) return;
    noteOn(midi);
  };

  useEffect(() => {
    const isTyping = () => {
      const el = document.activeElement;
      if (!el) return false;
      const tag = el.tagName;
      return (
        tag === "INPUT" ||
        tag === "TEXTAREA" ||
        tag === "SELECT" ||
        el.isContentEditable
      );
    };
    const down = (e) => {
      if (e.repeat) return;
      if (isTyping()) return;
      const key = e.key.toLowerCase();
      if (key === "arrowup") {
        e.preventDefault();
        setBaseOctave((o) => Math.min(7, o + 1));
        return;
      }
      if (key === "arrowdown") {
        e.preventDefault();
        setBaseOctave((o) => Math.max(0, o - 1));
        return;
      }
      if (KEY_MAP[key] !== undefined) {
        let midi = baseMidi + KEY_MAP[key];
        if (scaleLock) midi = nearestAllowed(midi);
        heldKeys.current[key] = midi;
        noteOn(midi);
      }
    };
    const up = (e) => {
      if (isTyping()) return;
      const key = e.key.toLowerCase();
      if (heldKeys.current[key] !== undefined) {
        noteOff(heldKeys.current[key]);
        delete heldKeys.current[key];
      }
    };
    window.addEventListener("keydown", down);
    window.addEventListener("keyup", up);
    return () => {
      window.removeEventListener("keydown", down);
      window.removeEventListener("keyup", up);
    };
  }, [noteOn, noteOff, baseMidi, scaleLock, rootPc, scaleId]);

  // Keep the Sound dropdown in sync with the engine (e.g. after loading a project,
  // where engine.instrument is restored directly).
  useEffect(() => {
    if (view === "piano" && engine.instrument && engine.instrument !== instrument) {
      setInstrument(engine.instrument);
    }
  });

  const changeInstrument = async (id) => {
    setInstrument(id);
    engine.ensureStarted();
    engine.setInstrument(id);
    const inst = INSTRUMENTS[id];
    if (inst && inst.sampler && !engine.samplesReady(inst.sampler)) {
      setPianoLoading(true);
      try {
        await engine.loadSamples(inst.sampler);
      } catch (e) {}
      setPianoLoading(false);
    }
  };

  const setViewMode = (v) => {
    setView(v);
    engine.ensureStarted();
    if (v === "bass") {
      engine.setInstrument("bassreal");
      if (!engine.samplesReady("bass")) {
        setPianoLoading(true);
        engine.loadSamples("bass").finally(() => setPianoLoading(false));
      }
    } else {
      engine.setInstrument(instrument);
    }
  };

  // Preload real samples for the default instrument so the piano sounds real on first play.
  useEffect(() => {
    const inst = INSTRUMENTS[instrument];
    if (inst && inst.sampler && !engine.samplesReady(inst.sampler)) {
      setPianoLoading(true);
      engine.loadSamples(inst.sampler).finally(() => setPianoLoading(false));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // build visible keys
  const startMidi = baseMidi;
  const endMidi = baseMidi + VISIBLE_OCTAVES * 12; // inclusive top C
  const whites = [];
  const blacks = [];
  for (let m = startMidi; m <= endMidi; m++) {
    if (WHITE_PC.includes(m % 12)) whites.push(m);
  }
  for (let m = startMidi; m < endMidi; m++) {
    if (BLACK_PC.includes(m % 12)) blacks.push(m);
  }
  const whiteW = 100 / whites.length;
  const blackW = whiteW * 0.6;

  return (
    <div className="flex flex-col h-full" data-testid="piano-synth">
      <div className="flex items-center justify-between px-3 py-2 border-b border-[#262626]">
        <div className="flex items-center gap-3">
          <h3 className="font-display text-sm uppercase tracking-widest text-neutral-300 flex items-center gap-1.5">
            Synth Keys
            <InfoDot section="synth" label="How the Synth & Piano work" />
          </h3>
          <div className="flex rounded-sm border border-[#2a2a2a] overflow-hidden">
            <button
              data-testid="view-piano"
              onClick={() => setViewMode("piano")}
              className="px-2.5 py-1 text-[9px] hw-label transition-colors"
              style={view === "piano" ? { background: "#00ff66", color: "#000" } : { color: "#a3a3a3" }}
            >
              Piano
            </button>
            <button
              data-testid="view-bass"
              onClick={() => setViewMode("bass")}
              className="px-2.5 py-1 text-[9px] hw-label transition-colors flex items-center gap-1"
              style={view === "bass" ? { background: "#ffb800", color: "#000" } : { color: "#a3a3a3" }}
            >
              <Guitar size={10} /> Bass
            </button>
          </div>
          <div className="flex items-center gap-1">
            <button
              data-testid="octave-down"
              onClick={() => setBaseOctave((o) => Math.max(0, o - 1))}
              className="w-6 h-6 rounded-sm border border-[#404040] flex items-center justify-center hover:bg-[#242424] transition-colors"
            >
              <ChevronLeft size={13} />
            </button>
            <span className="font-mono-data text-[10px] text-[#00ff66] w-16 text-center" data-testid="octave-display">
              C{baseOctave}–C{baseOctave + VISIBLE_OCTAVES}
            </span>
            <button
              data-testid="octave-up"
              onClick={() => setBaseOctave((o) => Math.min(7, o + 1))}
              className="w-6 h-6 rounded-sm border border-[#404040] flex items-center justify-center hover:bg-[#242424] transition-colors"
            >
              <ChevronRight size={13} />
            </button>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            data-testid="rec-notes-btn"
            onClick={() => {
              engine.ensureStarted();
              setRecNotes(engine.toggleRecordNotes());
            }}
            className="flex items-center gap-1 px-2 py-1 rounded-sm text-[9px] hw-label border transition-colors"
            style={
              recNotes
                ? { borderColor: "#ff3333", color: "#ff3333", background: "rgba(255,51,51,0.12)" }
                : { borderColor: "#2a2a2a", color: "#a3a3a3" }
            }
            title="Arm note recording — play keys while the song plays to record a part (auto-quantized)"
          >
            <Circle size={9} fill={recNotes ? "#ff3333" : "none"} className={recNotes ? "led-pulse" : ""} /> REC
          </button>
          <button
            data-testid="clear-notes-btn"
            onClick={() => engine.started && engine.clearNotes()}
            className="flex items-center gap-1 px-2 py-1 rounded-sm text-[9px] hw-label border border-[#2a2a2a] text-neutral-500 hover:text-[#ff3333] transition-colors"
            title="Erase the synth/melody part (including anything the AI Start added)"
          >
            <Eraser size={10} /> Clear Melody
          </button>
          <span className="hw-label text-[8px] ml-1">Key</span>
          <select
            data-testid="scale-root"
            value={rootPc}
            onChange={(e) => setRootPc(Number(e.target.value))}
            className="bg-[#050505] border border-[#2a2a2a] rounded-sm px-1.5 py-1 text-[10px] font-mono-data text-[#a855f7] outline-none focus:border-[#a855f7] cursor-pointer"
          >
            {NOTE_NAMES.map((n, i) => (
              <option key={i} value={i} className="bg-[#141414]">{n}</option>
            ))}
          </select>
          <select
            data-testid="scale-type"
            value={scaleId}
            onChange={(e) => setScaleId(e.target.value)}
            className="bg-[#050505] border border-[#2a2a2a] rounded-sm px-1.5 py-1 text-[10px] font-mono-data text-[#a855f7] outline-none focus:border-[#a855f7] cursor-pointer"
          >
            {Object.entries(SCALES).map(([id, s]) => (
              <option key={id} value={id} className="bg-[#141414]">{s.label}</option>
            ))}
          </select>
          <button
            data-testid="scale-lock-toggle"
            onClick={() => setScaleLock((v) => !v)}
            title="Scale Lock — hides out-of-key notes so you can't play a wrong one"
            className="flex items-center gap-1 px-2 py-1 rounded-sm text-[9px] hw-label border transition-colors"
            style={
              scaleLock
                ? { borderColor: "#a855f7", color: "#a855f7", background: "rgba(168,85,247,0.15)" }
                : { borderColor: "#2a2a2a", color: "#a3a3a3" }
            }
          >
            <Lock size={9} /> Lock
          </button>
          <span className="hw-label text-[8px] ml-1">Sound</span>
          <select
            data-testid="instrument-select"
            value={instrument}
            onChange={(e) => changeInstrument(e.target.value)}
            className="bg-[#050505] border border-[#2a2a2a] rounded-sm px-2 py-1 text-[10px] font-mono-data text-[#00ff66] outline-none focus:border-[#00ff66] cursor-pointer"
          >
            {INSTRUMENT_LIST.map((i) => (
              <option key={i.id} value={i.id} className="bg-[#141414]">
                {i.label}
              </option>
            ))}
          </select>
          {pianoLoading && (
            <span data-testid="piano-loading" className="hw-label text-[8px] text-[#ffb800] animate-pulse ml-1">
              loading real sound…
            </span>
          )}
        </div>
      </div>

      <div
        className="flex items-center gap-1 px-3 py-1.5 border-b border-[#262626] overflow-x-auto"
        data-testid="chord-assistant"
      >
        <span className="hw-label text-[8px] mr-1 shrink-0" style={{ color: "#a855f7" }}>Chords</span>
        {chords.map((c, i) => (
          <button
            key={i}
            data-testid={`chord-${i}`}
            onPointerDown={() => c.notes.forEach((m) => noteOn(m))}
            onPointerUp={() => c.notes.forEach((m) => noteOff(m))}
            onPointerLeave={() => c.notes.forEach((m) => noteOff(m))}
            style={{ touchAction: "none" }}
            className="shrink-0 px-2.5 py-1 rounded-sm border border-[#2a2a2a] text-[10px] font-mono-data text-[#a855f7] hover:border-[#a855f7] hover:bg-[#a855f7]/10 active:bg-[#a855f7]/25 transition-colors"
          >
            {c.label}
          </button>
        ))}
      </div>

      {view === "piano" && (
      <div className="flex-1 p-3 flex items-stretch justify-center min-h-0">
        <div className="relative flex w-full" style={{ maxWidth: whites.length * 46 }}>
          {whites.map((m) => {
            const offset = m - baseMidi;
            const label = OFFSET_LABEL[offset];
            const isC = m % 12 === 0;
            const dim = scaleLock && !isAllowed(m);
            return (
              <div
                key={m}
                data-testid={`key-${m}`}
                onPointerDown={() => pressKey(m)}
                onPointerUp={() => noteOff(m)}
                onPointerLeave={() => noteOff(m)}
                onPointerEnter={(e) => e.buttons === 1 && pressKey(m)}
                className={`flex-1 rounded-b-sm border border-neutral-700 flex flex-col items-center justify-end pb-1 transition-colors ${dim ? "cursor-not-allowed" : "cursor-pointer"}`}
                style={{ touchAction: "none", background: active[m] ? "#00e5ff" : dim ? "linear-gradient(180deg,#5a5a5a,#3d3d3d)" : "linear-gradient(180deg,#f4f4f4,#c8c8c8)", opacity: dim ? 0.5 : 1 }}
              >
                {isC && <span className="text-[7px] text-neutral-500 font-mono-data">C{Math.floor(m / 12) - 1}</span>}
                {label && <span className="text-[8px] text-neutral-600 font-mono-data uppercase">{label}</span>}
              </div>
            );
          })}
          {/* black keys */}
          <div className="absolute top-0 left-0 h-[62%] w-full pointer-events-none">
            {blacks.map((m) => {
              const lowerWhiteIdx = whites.indexOf(m - 1);
              const left = (lowerWhiteIdx + 1) * whiteW - blackW / 2;
              const offset = m - baseMidi;
              const label = OFFSET_LABEL[offset];
              const dim = scaleLock && !isAllowed(m);
              return (
                <div
                  key={m}
                  data-testid={`key-${m}`}
                  onPointerDown={() => pressKey(m)}
                  onPointerUp={() => noteOff(m)}
                  onPointerLeave={() => noteOff(m)}
                  className={`absolute rounded-b-sm border border-black flex items-end justify-center pb-0.5 pointer-events-auto transition-colors ${dim ? "cursor-not-allowed" : "cursor-pointer"}`}
                  style={{
                    left: `${left}%`,
                    width: `${blackW}%`,
                    height: "100%",
                    touchAction: "none",
                    background: active[m] ? "#00e5ff" : "linear-gradient(180deg,#1c1c1c,#000)",
                    opacity: dim ? 0.35 : 1,
                    zIndex: 10,
                  }}
                >
                  {label && <span className="text-[7px] text-neutral-400 font-mono-data uppercase">{label}</span>}
                </div>
              );
            })}
          </div>
        </div>
      </div>
      )}

      {view === "bass" && (
        <div className="flex-1 p-3 flex flex-col gap-1 min-h-0" data-testid="bass-fretboard">
          <div className="flex items-center gap-0.5 pl-6">
            {Array.from({ length: 13 }).map((_, f) => (
              <div key={f} className="flex-1 text-center text-[8px] font-mono-data text-neutral-600">
                {f === 0 ? "0" : [3, 5, 7, 9, 12].includes(f) ? "•" : ""}
              </div>
            ))}
          </div>
          {[["G", 43], ["D", 38], ["A", 33], ["E", 28]].map(([name, open], si) => (
            <div key={name} className="flex items-center gap-1 flex-1">
              <span className="w-5 text-center font-mono-data text-[11px] text-[#ffb800] shrink-0">{name}</span>
              <div className="flex-1 flex gap-0.5 relative items-center">
                <div
                  className="absolute left-0 right-0 bg-neutral-500 pointer-events-none"
                  style={{ height: `${1 + si * 0.7}px` }}
                />
                {Array.from({ length: 13 }).map((_, fret) => {
                  const m = open + fret;
                  const dim = scaleLock && !isAllowed(m);
                  const isRoot = (((m % 12) + 12) % 12) === rootPc;
                  const on = active[m];
                  return (
                    <button
                      key={fret}
                      data-testid={`fret-${name}-${fret}`}
                      onPointerDown={() => pressKey(m)}
                      onPointerUp={() => noteOff(m)}
                      onPointerLeave={() => noteOff(m)}
                      onPointerEnter={(e) => e.buttons === 1 && pressKey(m)}
                      disabled={dim}
                      className={`relative z-10 flex-1 h-full rounded-sm border flex items-center justify-center text-[9px] font-mono-data transition-colors ${dim ? "cursor-not-allowed" : "cursor-pointer"}`}
                      style={{
                        touchAction: "none",
                        background: on ? "#00e5ff" : isRoot ? "rgba(255,184,0,0.22)" : "#141414",
                        borderColor: on ? "#00e5ff" : isRoot ? "#ffb800" : "#2a2a2a",
                        color: on ? "#000" : dim ? "#3d3d3d" : isRoot ? "#ffb800" : "#8a8a8a",
                        opacity: dim ? 0.45 : 1,
                      }}
                    >
                      {NOTE_NAMES[((m % 12) + 12) % 12]}
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
